import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowRightLeft } from 'lucide-react';
import { SellCreditsCashModal, type SellCreditsCashFormValues } from '@kioskgaming/ui';
import { createShopCashCredits, fetchDashboard } from '../../services/api';
import AvailableCreditsCallout from './AvailableCreditsCallout';

export type SellCreditsCashToShopTriggerProps = {
  shopId: string;
  shopName: string;
  /** Shop cost as decimal 0–1 (buyer cost rate). */
  shopCostRate?: string | number;
  onSuccess?: () => void;
  buttonText?: string;
  size?: 'sm' | 'md';
  className?: string;
  disabled?: boolean;
};

export default function SellCreditsCashToShopTrigger({
  shopId,
  shopName,
  shopCostRate,
  onSuccess,
  buttonText = 'Sell (cash)',
  size = 'md',
  className = '',
  disabled = false,
}: SellCreditsCashToShopTriggerProps) {
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveCostRate = (() => {
    const n = Number(shopCostRate ?? 0);
    return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0;
  })();

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const res = await fetchDashboard();
      if (res.success && res.data) setBalance(res.data.credit_balance);
      else setBalance(null);
    } catch {
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadBalance();
    setError(null);
  }, [open, loadBalance]);

  const handleSubmit = async (values: SellCreditsCashFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await createShopCashCredits(shopId, {
        credits: values.creditAmount,
        payment_method: values.paymentMethod,
        received_at: values.receivedAt,
        external_reference: values.externalReference,
        note: values.note,
      });
      if (!res.success) {
        setError(res.message || 'Transfer failed');
        toast.error(res.message || 'Transfer failed');
        return;
      }
      toast.success('Credits sold (cash) and transferred');
      setOpen(false);
      onSuccess?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Transfer failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const btnClass =
    size === 'sm'
      ? 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-orange-900 hover:bg-orange-100 disabled:opacity-50'
      : 'inline-flex items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-900 hover:bg-orange-100 disabled:opacity-50';

  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={`${btnClass} ${className}`}
      >
        <ArrowRightLeft className={iconClass} aria-hidden />
        {buttonText}
      </button>

      <SellCreditsCashModal
        open={open}
        onClose={() => setOpen(false)}
        flow="agent_shop"
        buyerName={shopName}
        buyerCostRate={effectiveCostRate}
        submitting={submitting}
        error={error}
        onSubmit={handleSubmit}
      />

      {open ? (
        <div className="sr-only">
          <AvailableCreditsCallout balance={balance} loading={balanceLoading} />
        </div>
      ) : null}
    </>
  );
}
